export default class ReadingCoachSelect {
  constructor (element) {
    this.element = element
    this.lastTypedChar = ''
    this.lastMatchingIndex = null

    this.onAfterUpdate = () => {}

    this.dropdown = this.element.querySelector('.js-selected-option')
    this.live = this.element.querySelector('.js-live')
    this.list = document.querySelector(`.${this.element.dataset.selector}`)
    this.options = [...this.list.querySelectorAll('.js-option-button')]

    if (!('anchorName' in document.documentElement.style)) {
      this.element.appendChild(this.list)
    }

    const initialOption = this.list.querySelector('[aria-selected="true"]')

    this.focusedIndex = this.options.indexOf(initialOption)

    this.dropdown.addEventListener('click', () => {
      this.toggleSelect()
    })

    this.list.addEventListener('click', ({ target }) => {
      const button = target.closest('button')

      if (!button) return

      this.updateSelectedOption(this.options.indexOf(button))
    }, false)

    this.dropdown.addEventListener('keydown', (event) => {
      if (event.metaKey || event.key === 'Tab') return

      event.preventDefault()

      const openKeys = ['Enter', ' ', 'ArrowUp', 'ArrowDown']
      const closeKeys = ['Escape']

      if (openKeys.includes(event.key) && !this.list.classList.contains('opened')) {
        this.openSelect()
      } else if (closeKeys.includes(event.key) && this.list.classList.contains('opened')) {
        this.closeSelect()
      } else if (!this.list.classList.contains('opened')) {
        const index = this.checkAlphaNumericKeyPress(event.key)
        if (index !== null) {
          this.openSelect()
          this.options[index].focus()
          this.updateSelectedOption(index, true, true)
        }
      }
    })

    this.list.addEventListener('keydown', (event) => {
      if (event.metaKey || !this.list.classList.contains('opened')) return

      event.preventDefault()

      const closeKeys = ['Escape']
      const navigationKeys = ['ArrowUp', 'ArrowDown']
      const selectKeys = ['Enter', ' ']

      if (closeKeys.includes(event.key)) {
        this.closeSelect()
      } else if (navigationKeys.includes(event.key)) {
        this.navigateList(event.key)
      } else if (selectKeys.includes(event.key)) {
        this.updateSelectedOption(this.options.indexOf(event.target))
      } else {
        this.handleAlphaNumericKeyPress(event.key)
      }
    })
  }

  /**
   * Handles arrow key navigation of the options list
   * @param {string} key - String representation of the pressed key
   */
  navigateList = (key) => {
    const lastIndex = this.options.length - 1

    if (key === 'ArrowUp' && this.focusedIndex > 0) {
      this.focusedIndex = this.focusedIndex - 1
    } else if (key === 'ArrowDown' && this.focusedIndex < lastIndex) {
      this.focusedIndex = this.focusedIndex + 1
    } else if (key === 'ArrowDown' && this.focusedIndex === lastIndex) {
      this.focusedIndex = 0
    } else if (key === 'ArrowUp' && this.focusedIndex === 0) {
      this.focusedIndex = lastIndex
    }

    this.options[this.focusedIndex].focus()
  }

  /**
   * Updates the select with the option at the given index
   * @param {int} index
   * @param {bool} skipFocusReset - Skips setting the focus on the parent select
   * @param {book} skipClose - Skips closing the select after an option is selected
   */
  updateSelectedOption = (index, skipFocusReset, skipClose) => {
    const button = this.options[index]

    this.dropdown.querySelector('.js-text').textContent = button.dataset.label
    this.element.dataset.value = button.dataset.value

    this.options.forEach((option, optionIndex) => {
      if (optionIndex === index) {
        option.setAttribute('aria-selected', 'true')
      } else {
        option.setAttribute('aria-selected', 'false')
      }
    })

    this.updateIcon(button)

    if (!skipClose) {
      this.closeSelect(skipFocusReset)
    }

    this.focusedIndex = index

    this.onAfterUpdate(button.dataset.value)
  }

  /**
   * Checks the options against the key pressed and returns the index of the
   * option that should be selected
   * @param {string} key - String representation of the pressed key
   */
  checkAlphaNumericKeyPress = (key) => {
    const typedChar = key.toLowerCase()

    const matchingOptions = Array.from(this.options).filter((option) =>
      option.dataset.value.toLowerCase().startsWith(typedChar)
    )

    let index = null

    if (matchingOptions.length === 1) {
      index = Array.from(this.options).indexOf(matchingOptions[0])
    } else if (matchingOptions.length > 1) {
      if (this.lastTypedChar !== typedChar) {
        this.lastMatchingIndex = 0
      } else {
        this.lastMatchingIndex = Math.min(this.lastMatchingIndex + 1, matchingOptions.length - 1)
      }

      const value = matchingOptions[this.lastMatchingIndex]
      index = Array.from(this.options).indexOf(value)
    }

    this.lastTypedChar = typedChar

    return index
  }

  /**
   * Keys the pressed key and updates the option accordingly
   * @param {string} key - String representation of the pressed key
   */
  handleAlphaNumericKeyPress = (key) => {
    const index = this.checkAlphaNumericKeyPress(key)

    if (index === null) return

    if (!this.list.classList.contains('opened')) {
      this.openSelect()
    }

    this.options[index].focus()
    this.updateSelectedOption(index, true, true)
  }

  /**
   * Updates the parent select's icon with the chosen option's icon
   * @param {DOMElement} selectedOption
   */
  updateIcon = (selectedOption) => {
    const selectedIcon = selectedOption.querySelector('.icon')
    const thisIcon = this.dropdown.querySelector('.icon')
    if (selectedIcon.classList.contains('icon--empty')) {
      thisIcon.classList.add('icon--empty')
      thisIcon.style.setProperty('--icon', '')
    } else {
      thisIcon.classList.remove('icon--empty')
      thisIcon.style.setProperty('--icon', selectedIcon.style.getPropertyValue('--icon'))
    }
  }

  /**
   * Sets a random option for the select
   */
  setRandom = () => {
    this.updateSelectedOption(
      Math.floor(Math.random() * this.options.length),
      true
    )
  }

  /**
   * Opens the select dropdown
   */
  openSelect = () => {
    document.addEventListener('click', this.onClickOutside)

    this.list.classList.add('opened')
    this.list.setAttribute('aria-expanded', true)

    const selected = this.list.querySelector('[aria-selected="true"]')
    if (selected) {
      selected.focus()
    } else {
      this.options[0].focus()
      this.focusedIndex = 0
    }
  }

  /**
   * Closes the select.
   * @param {bool} skipFocus - If true, don't refocus the select
   */
  closeSelect = (skipFocus) => {
    document.removeEventListener('click', this.onClickOutside)

    this.list.classList.remove('opened')
    this.list.setAttribute('aria-expanded', false)

    if (skipFocus !== true) {
      this.dropdown.focus()
    }

    this.lastMatchingIndex = null
    this.lastTypedChar = ''
  }

  /**
   * Toggles the select open and closed
   */
  toggleSelect = () => {
    if (this.list.classList.contains('opened')) {
      this.closeSelect()
    } else {
      this.openSelect()
    }
  }

  /**
   * Handles closing the select when the user clicks outside of the options list
   * @param {MouseEvent} event
   */
  onClickOutside = (event) => {
    const inSelect = event.target.closest('.js-reading-coach-select')
    if (inSelect && inSelect === this.element) return

    this.closeSelect()
  }
}
